/** Which of the two applications this bundle is behaving as.
 *
 * The operator console used to be `/admin/*` inside the customer dashboard:
 * one origin, one bundle, one session. That is convenient and wrong, and the
 * reason is `localStorage`.
 *
 * Access and refresh tokens are kept in `localStorage`, which the browser
 * scopes to an *origin*. While the console lived on `app.talaricloud.com`,
 * any script that ran on any customer-facing page could read an operator's
 * token — a cross-site scripting bug in a service list, a log viewer, a form,
 * anywhere — and that token opens every customer's account. Separating the
 * hostname separates the token store, so the same bug reaches nothing: the
 * console's tokens live on `admin.talaricloud.com` and are unreadable from
 * the customer origin, by the browser, not by our code.
 *
 * It also separates the two failure domains. A bug that breaks the customer
 * dashboard used to break the operator console with it — during an incident,
 * which is exactly when somebody needs the console.
 *
 * This is deliberately decided at runtime from the hostname rather than baked
 * in at build time. One image serves both surfaces, so there is one thing to
 * build, one thing to publish and one thing to deploy, and the two can never
 * drift to different versions of the same code.
 *
 * Note what this is *not*: it is not an authorisation boundary. The API
 * decides that, by checking `is_admin` and a second factor on every admin
 * request — see `deps.get_admin_user`. Anybody can visit the console's
 * hostname; it just answers 403 to anyone who should not be there.
 */
export type Surface = 'app' | 'admin'

/** Hostnames whose console is the admin one. A subdomain named exactly
 *  `admin`, so `admin.talaricloud.com` and nothing that merely contains it —
 *  `admin-portal.example.com` is somebody else's site, not ours. */
function looksLikeConsole(hostname: string): boolean {
  return hostname === 'admin' || hostname.startsWith('admin.')
}

export function surfaceFor(hostname: string, override?: string): Surface {
  // Development runs both on localhost, where there is no hostname to read.
  // `VITE_SURFACE=admin npm run dev` opens the console instead.
  if (override === 'admin' || override === 'app') return override
  return looksLikeConsole(hostname.toLowerCase()) ? 'admin' : 'app'
}

export const SURFACE: Surface = surfaceFor(
  typeof window === 'undefined' ? '' : window.location.hostname,
  import.meta.env.VITE_SURFACE as string | undefined,
)

export const isConsole = SURFACE === 'admin'

/** Where the operator console lives, as seen from the customer dashboard.
 *
 * An operator still needs a way across, and after the split the old `/admin`
 * links point at routes this origin no longer registers — four items in the
 * sidebar that go nowhere. This turns them into one link that leaves.
 *
 * Derived from the current host rather than hard-coded, so a staging or
 * self-hosted installation on another domain gets its own console rather than
 * a link to ours: `app.example.com` → `admin.example.com`.
 */
export function consoleUrl(hostname = window.location.hostname): string {
  const labels = hostname.split('.')
  // A bare host with no domain (localhost, an IP) has nowhere to put the
  // subdomain. Dev runs both surfaces on one origin, so point at the routes.
  if (labels.length < 3) return '/admin'
  return `https://admin.${labels.slice(1).join('.')}/`
}
